/**
 * Live TV "lowest final price" cart messaging gate.
 *
 * The note is a price claim that only holds while that cart line's auction is still on air. The cart
 * line properties it used to be gated on (_itemSource, _auctionCode) are captured at add-to-cart time
 * and never change, so the message survived the auction moving to Missed Auction / FPC.
 *
 * Liquid now renders the note hidden with the line's auction code on data-ltv-auction-code; this
 * unhides only the lines whose code is currently on air. A line with no auction code stays hidden.
 *
 * Two sources, same underlying metafield:
 *   1. live-tv-on-air-seed.liquid, server-rendered into the page. Painted first so the correct state
 *      shows without waiting on the network, and used as the fallback when the proxy cannot be
 *      reached at all (tunnel interstitial in dev, outage in production). Only trusted while fresh -
 *      it is as old as the last server render, and the drawer opens with no server round-trip.
 *   2. /apps/live-tv/currently-on-air. Authoritative whenever it answers: a valid response always
 *      wins, including isLive:false and success:false, both of which hide every line.
 *
 * Fail safe is hidden: no fresh seed and no usable response means nothing shows. Failures log to the
 * console; there is no error UI.
 *
 * ---------------------------------------------------------------------------------------------
 * Second responsibility: rewriting _itemSource from TV to MA once a line's auction has ended.
 *
 * A line keeps _itemSource: TV forever, so an item bought after its auction ended is placed as TV.
 * Placed orders cannot have line attributes edited, so it has to happen in the cart. Best-effort by
 * design - checkout straight from the drawer or a /checkout link may still order as TV.
 *
 * The fail-safe direction is the OPPOSITE of the message, and the two must not be collapsed:
 * hiding a message is free and reversible, mutating a cart line is neither. So the flip runs only on
 * a positive, successful endpoint response. Notably the server-rendered seed does NOT authorise a
 * mutation - it stands in exactly when the endpoint is unreachable, which is a "leave TV" case.
 *
 *   endpoint says line is on air      -> show, leave TV
 *   endpoint says line is not on air  -> hide, flip to MA
 *   isLive:false (nothing on air)     -> hide, flip to MA
 *   success:false / network / bad JSON-> hide, LEAVE TV
 *   line has no _auctionCode          -> hide, LEAVE TV
 */
(function () {
  'use strict';

  if (window.__tjcLiveTvCartOnAir) return;
  window.__tjcLiveTvCartOnAir = true;

  var ENDPOINT = '/apps/live-tv/currently-on-air';
  var LINE_SELECTOR = '[data-ltv-auction-code]';
  var SEED_SELECTOR = '#ltv-on-air-seed';
  /* Cart re-renders reuse this payload; once it is older than the TTL the next re-render refetches,
     so a cart left open through the end of an auction still drops the message. */
  var CACHE_TTL_MS = 60000;
  /* How long a server-rendered seed may stand in for a live answer. Auctions turn over in minutes,
     so anything older than this is not evidence the line is still on air. */
  var SEED_MAX_AGE_MS = 120000;
  var RERENDER_DEBOUNCE_MS = 50;

  var CART_URL = '/cart.js';
  var CART_CHANGE_URL = '/cart/change.js';

  var cache = null;
  var pendingRefresh = null;
  /* change.js re-renders the cart, which fires the events this script listens on. A flipped line is
     MA and no longer matches, but this stops a re-render mid-pass starting a second overlapping one. */
  var flipInFlight = false;

  function normalizeCode(value) {
    return value == null ? '' : String(value).trim().toLowerCase();
  }

  function addCode(codes, value) {
    var code = normalizeCode(value);
    if (code) codes.add(code);
  }

  function addAuctionCodes(codes, auctions) {
    if (!Array.isArray(auctions)) return;
    for (var i = 0; i < auctions.length; i++) {
      if (auctions[i]) addCode(codes, auctions[i].auctionCode);
    }
  }

  /**
   * data.auctionCode is the parent package code, but a line added from a group (GLA) or MLA auction
   * carries the child code - so the group/MLA arrays are not optional.
   *
   * @param {any} payload
   * @returns {Set<string>}
   */
  function collectOnAirCodes(payload) {
    var codes = new Set();
    if (!payload || payload.isLive !== true) return codes;

    var data = payload.data;
    if (!data || typeof data !== 'object') return codes;

    addCode(codes, data.auctionCode);
    addCode(codes, data.packageCode);
    addAuctionCodes(codes, data.groupAuctions);
    addAuctionCodes(codes, data.mlaAuctions);
    return codes;
  }

  /**
   * The server-rendered fallback. Returns null when absent or too old to stand as evidence.
   *
   * @returns {Set<string> | null}
   */
  function readSeed() {
    var seed = document.querySelector(SEED_SELECTOR);
    if (!seed) return null;

    var renderedAt = Number(seed.getAttribute('data-ltv-rendered-at')) * 1000;
    if (!Number.isFinite(renderedAt) || Date.now() - renderedAt > SEED_MAX_AGE_MS) return null;

    var codes = new Set();
    var raw = seed.getAttribute('data-ltv-codes') || '';
    var parts = raw.split('|');
    for (var i = 0; i < parts.length; i++) addCode(codes, parts[i]);
    return codes;
  }

  /**
   * Three distinct outcomes, deliberately not collapsed into two:
   *
   *   answered:false                 transport failed (HTTP error, network, bad JSON). No answer, so
   *                                  the seed may stand for the message and nothing may be mutated.
   *   answered:true  authoritative:false
   *                                  the app replied with success:false. That IS an answer for the
   *                                  message - hide, overriding any seed - but it is an error, not a
   *                                  statement that the auction ended, so it must not mutate.
   *   answered:true  authoritative:true
   *                                  success:true, whether isLive is true or false. Codes are the
   *                                  truth: show what matches, and flip what does not.
   *
   * @returns {Promise<{answered: boolean, authoritative: boolean, codes: Set<string>}>}
   */
  function fetchOnAirCodes() {
    return fetch(ENDPOINT, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('currently-on-air responded ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        if (!payload || payload.success !== true) {
          console.warn('[live-tv-cart] on-air endpoint returned an error payload', payload);
          return { answered: true, authoritative: false, codes: new Set() };
        }
        return { answered: true, authoritative: true, codes: collectOnAirCodes(payload) };
      })
      .catch(function (error) {
        console.warn('[live-tv-cart] on-air lookup failed; falling back to server-rendered seed', error);
        return { answered: false, authoritative: false, codes: new Set() };
      });
  }

  /** @returns {Promise<{answered: boolean, authoritative: boolean, codes: Set<string>}>} */
  function getOnAirCodes() {
    var now = Date.now();
    if (!cache || now - cache.at > CACHE_TTL_MS) {
      cache = { at: now, promise: fetchOnAirCodes() };
    }
    return cache.promise;
  }

  /** @param {Set<string>} codes */
  function applyCodes(codes) {
    var lines = document.querySelectorAll(LINE_SELECTOR);
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var code = normalizeCode(line.getAttribute('data-ltv-auction-code'));
      if (code && codes.has(code)) {
        line.removeAttribute('hidden');
      } else {
        line.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Strict on purpose. _itemSource is a three-value enum (TV / MA / FPC) and a pricing control, not a
   * label: the discount function excludes tv and ma lines from stacked promotions. Anything that is
   * not exactly TV is either already handled or not ours, and must be left alone.
   *
   * @param {any} properties
   * @returns {string}
   */
  function itemSourceOf(properties) {
    return properties && typeof properties._itemSource === 'string' ? properties._itemSource.trim() : '';
  }

  /**
   * The first line whose auction has demonstrably ended, or null. A line with no _auctionCode is
   * skipped: without a code there is no evidence either way.
   *
   * @param {any} cart
   * @param {Set<string>} onAirCodes
   * @returns {any}
   */
  function findEndedLine(cart, onAirCodes) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (itemSourceOf(item && item.properties) !== 'TV') continue;

      var code = normalizeCode(item.properties._auctionCode);
      if (!code || onAirCodes.has(code)) continue;

      return item;
    }
    return null;
  }

  /**
   * change.js REPLACES the properties object wholesale, so every existing property is sent back and
   * only _itemSource is altered - AMS webcheck, the Buy All discount function and order tagging all
   * read properties that would otherwise be destroyed. Quantity is deliberately omitted so it stays
   * unchanged, which also keeps it correct when the line merges into an existing MA line.
   *
   * Addressed by line key, never variant id: one variant can sit on several lines under different
   * auction codes.
   *
   * @param {any} item
   * @returns {Promise<any>} the updated cart
   */
  function flipLineToMissedAuction(item) {
    var properties = {};
    for (var name in item.properties) {
      if (Object.prototype.hasOwnProperty.call(item.properties, name)) {
        properties[name] = item.properties[name];
      }
    }
    properties._itemSource = 'MA';

    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: item.key, properties: properties }),
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/change.js responded ' + response.status);
      return response.json();
    });
  }

  /**
   * Sequential by construction: the Ajax cart API serialises poorly and concurrent writes produce
   * 422s and lost updates. Each response is the updated cart and drives the next lookup, so line keys
   * are never reused across calls - a rewritten line gets a new key, and a line that merges into an
   * existing MA line disappears from the list entirely.
   *
   * @param {Set<string>} onAirCodes
   */
  function flipEndedLines(onAirCodes) {
    if (flipInFlight) return;
    flipInFlight = true;

    fetch(CART_URL, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js responded ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        /* Every flip removes one line from the candidate set, so this can only over-provision. */
        var budget = cart && Array.isArray(cart.items) ? cart.items.length : 0;

        function step(currentCart, remaining) {
          if (remaining <= 0) return Promise.resolve();

          var target = findEndedLine(currentCart, onAirCodes);
          if (!target) return Promise.resolve();

          return flipLineToMissedAuction(target).then(function (updatedCart) {
            return step(updatedCart, remaining - 1);
          });
        }

        return step(cart, budget);
      })
      .catch(function (error) {
        console.warn('[live-tv-cart] could not rewrite ended Live TV lines to MA', error);
      })
      .then(function () {
        flipInFlight = false;
      });
  }

  /** After applyCodes, a still-hidden hook is a line whose auction is not on air. */
  function hasEndedLines() {
    var lines = document.querySelectorAll(LINE_SELECTOR);
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].hasAttribute('hidden')) return true;
    }
    return false;
  }

  function refresh() {
    if (!document.querySelector(LINE_SELECTOR)) return;

    var seed = readSeed();
    if (seed) applyCodes(seed);

    getOnAirCodes().then(function (result) {
      if (!result.answered) {
        /* No answer at all: leave the seed's paint if it made one, otherwise hide. Mutate nothing. */
        if (!seed) applyCodes(new Set());
        return;
      }

      applyCodes(result.codes);

      /* Only a success:true payload may rewrite a cart line. An error payload hides the message but
         is not evidence any auction ended. */
      if (result.authoritative && hasEndedLines()) flipEndedLines(result.codes);
    });
  }

  function scheduleRefresh() {
    if (pendingRefresh) return;
    pendingRefresh = window.setTimeout(function () {
      pendingRefresh = null;
      refresh();
    }, RERENDER_DEBOUNCE_MS);
  }

  function observeCartRerenders() {
    if (typeof MutationObserver !== 'function' || !document.body) return;

    /* The cart page morphs its section and the drawer swaps its contents; both replace the line nodes,
       which come back hidden. Only childList is observed - applyCodes writes attributes, so observing
       attributes here would loop. */
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches(LINE_SELECTOR) || node.querySelector(LINE_SELECTOR)) {
            scheduleRefresh();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    refresh();
    observeCartRerenders();
    document.addEventListener('cart:update', scheduleRefresh);
    document.addEventListener('cart-page:morphed', scheduleRefresh);
    document.addEventListener('cart-drawer:open', scheduleRefresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
